/**
 * The one view-model derivation for a meeting's speakers.
 *
 * `deriveMeetingSpeakers(meeting)` returns a render-ready `ISpeakerVM[]` plus the
 * lookup maps and counts every speaker surface needs, so no component has to
 * hand-roll colour / initials / status / dedupe logic again.
 *
 * Two paths:
 *   - PRIMARY — when the backend has attached the unified `speakers[]` read
 *     model (A5): each entry maps 1:1 to a VM; colour comes from the entry's
 *     profile override, else the slot's palette hue, else a name hash.
 *   - LEGACY  — for meetings served before the unified model existed: reproduces
 *     the derivation that lived inline in `routes/_auth/meetings/$id.tsx`
 *     (roster ∪ analysis names, `buildSpeakerColorMap`, `isUnidentifiedSpeaker`),
 *     so nothing regresses during the adoption sweep.
 *
 * Pure — no React, no hooks. Wrapped by `hooks/useMeetingSpeakers`.
 */

import type { ISpeakerEntry, ISpeakerProfile, TSpeakerEntryStatus } from "@lexicanon/shared"
import { isRawSpeakerLabel } from "@lexicanon/shared"
import type { I18n } from "@lingui/core"
import { msg } from "@lingui/core/macro"
import {
  buildSpeakerColorMap,
  normalizeSpeakerKey,
  resolveSpeakerColor,
  speakerColor,
  unnamedSlotColor,
} from "./speaker-colors"
import { speakerInitials } from "./speaker-format"
import {
  coerceSpeakerQuotes,
  detectIndexBase,
  isUnidentifiedSpeaker,
  normalizeAnalysis,
  positionalSpeakerLabelMap,
} from "./speaker-mapping"

const REMOTE_PREFIX = "Remote::"

/** One speaker, resolved for rendering. Extends the shared read-model entry. */
export interface ISpeakerVM extends ISpeakerEntry {
  /** Final CSS colour to paint this speaker's identity (override ?? palette ?? hash). */
  resolvedColor: string
  /** True when the speaker still needs human attention (unidentified or a pending suggestion). */
  needsAttention: boolean
  /** Canonical avatar/chip initials for {@link ISpeakerEntry.displayName}. */
  initials: string
  /**
   * Earliest segment start (seconds) this label was heard, from
   * `streaming_segments`. `null` when the meeting carries no segments for the
   * label (or the VM is synthetic / label-null).
   */
  firstSpokeSeconds: number | null
  /**
   * The single longest segment for this label — the "listen to this speaker"
   * target the unidentified card's play-dot seeks to. `null` when no segments.
   */
  longestSegment: { start: number; end: number } | null
  /**
   * Share of the meeting's total talk time, rounded to a whole percent
   * (`talkSeconds / Σ talkSeconds`). `null` when no talk time is recorded
   * meeting-wide (so a card can fall back rather than render `0%`).
   */
  talkPercent: number | null
  /**
   * How many timed `streaming_segments` belong to this speaker. Used by the
   * merge review ("N segments move"). `0` when the meeting carries no matching
   * segments (or the VM is synthetic / label-null with no display-name match).
   */
  segmentCount: number
}

export interface ISpeakerVMResult {
  /** One VM per entry/slot (merge duplicates kept distinct), in order, then an analysis-only tail. */
  speakers: ISpeakerVM[]
  /** Raw diarisation label (incl. `Remote::`) → VM. First-wins on duplicate labels. */
  byLabel: Map<string, ISpeakerVM>
  /** Display name → VM. First-wins, so merge duplicates resolve to the first slot. */
  byName: Map<string, ISpeakerVM>
  /** normalizeSpeakerKey(displayName | label) → resolvedColor. Superset of the legacy `$id.tsx` map. */
  colorMap: Map<string, string>
  /** Count of speakers with status "unidentified". */
  unidentifiedCount: number
  /** Count of speakers with status "suggested". */
  suggestedCount: number
}

/**
 * The subset of a meeting `deriveMeetingSpeakers` reads. `IMeetingDetail`
 * satisfies it structurally; the loose shape keeps the lib product-agnostic and
 * trivially testable.
 */
export interface IDeriveMeetingInput {
  speakers?: ISpeakerEntry[]
  speaker_names?: string[]
  talk_share?: number[]
  speaker_profiles?: ISpeakerProfile[]
  analysis?: ILegacyAnalysisSource | null
  streaming_segments?: ISegmentInput[]
}

/**
 * The subset of a streaming segment the VM mines for per-label stats. Structurally
 * satisfied by `IStreamingSegment`; `start`/`end` are optional so a bare
 * `{ speaker }[]` (all the legacy name-collection needs) still type-checks.
 */
interface ISegmentInput {
  speaker: string
  start?: number
  end?: number
}

/** Per-key segment stats: when first heard, longest single segment, how many segments. */
interface ISegmentStat {
  firstSpokeSeconds: number
  longestSegment: { start: number; end: number }
  segmentCount: number
}

/** Structural view of the analysis fields the legacy path mines for names. */
interface ILegacyAnalysisSource {
  attendee_contributions?: unknown
  speaker_quotes?: unknown
}

function isFarEnd(label: string): boolean {
  return label.startsWith(REMOTE_PREFIX)
}

function stripRemote(label: string): string {
  return label.startsWith(REMOTE_PREFIX) ? label.slice(REMOTE_PREFIX.length) : label
}

function needsAttentionFor(status: TSpeakerEntryStatus): boolean {
  return status === "unidentified" || status === "suggested"
}

/** Palette hue for a slot index, wrapping safely for any (even negative) slot. */
function paletteColor(slot: number): string {
  return unnamedSlotColor(slot)
}

/** Register both the display-name and label keys for a VM's colour (first-wins). */
function registerColor(colorMap: Map<string, string>, vm: ISpeakerVM): void {
  const nameKey = normalizeSpeakerKey(vm.displayName)
  if (!colorMap.has(nameKey)) colorMap.set(nameKey, vm.resolvedColor)
  if (vm.label !== null) {
    const labelKey = normalizeSpeakerKey(vm.label)
    if (!colorMap.has(labelKey)) colorMap.set(labelKey, vm.resolvedColor)
  }
}

/**
 * Fold `streaming_segments` into per-key stats in one pass: earliest start,
 * longest segment, and count. The getMeeting payload remaps `segment.speaker`
 * from the raw diarisation label to the resolved display name when a
 * label→name map exists, so the same map is keyed by whichever string the
 * payload actually carries. Segments without a numeric `start` are skipped.
 */
function deriveSegmentStats(segments: ISegmentInput[] | undefined): Map<string, ISegmentStat> {
  const stats = new Map<string, ISegmentStat>()
  if (!segments) return stats
  for (const seg of segments) {
    if (typeof seg.start !== "number") continue
    const start = seg.start
    const end = typeof seg.end === "number" ? seg.end : start
    const duration = Math.max(0, end - start)
    const existing = stats.get(seg.speaker)
    if (!existing) {
      stats.set(seg.speaker, { firstSpokeSeconds: start, longestSegment: { start, end }, segmentCount: 1 })
      continue
    }
    existing.segmentCount += 1
    if (start < existing.firstSpokeSeconds) existing.firstSpokeSeconds = start
    if (duration > existing.longestSegment.end - existing.longestSegment.start) {
      existing.longestSegment = { start, end }
    }
  }
  return stats
}

/**
 * The API remaps `streaming_segments[].speaker` to the resolved display name
 * once identities exist, while each VM's `label` stays the raw diarisation
 * id. Try label first (unnamed / unmapped meetings), then display name, then
 * the unprefixed label — so a named Lite row still gets a sample window.
 */
function statFor(vm: ISpeakerVM, segStats: Map<string, ISegmentStat>): ISegmentStat | undefined {
  if (vm.label !== null) {
    const byLabel = segStats.get(vm.label)
    if (byLabel) return byLabel
  }
  const byName = segStats.get(vm.displayName)
  if (byName) return byName
  if (vm.label !== null) {
    const stripped = stripRemote(vm.label)
    if (stripped !== vm.label) {
      const byStripped = segStats.get(stripped)
      if (byStripped) return byStripped
    }
  }
  return undefined
}

/**
 * Fill the segment-derived stats onto already-built VMs: first-spoke + longest
 * segment (keyed by label, then display name), segment count, and `talkPercent`
 * from each VM's `talkSeconds` over the meeting total. Runs once per derivation,
 * after the VMs (and their lookup maps, which share the same object refs) exist.
 */
function attachSegmentStats(speakers: ISpeakerVM[], segStats: Map<string, ISegmentStat>): void {
  let totalTalk = 0
  for (const vm of speakers) if (vm.talkSeconds > 0) totalTalk += vm.talkSeconds
  for (const vm of speakers) {
    const stat = statFor(vm, segStats)
    // The backend now computes first-spoke across BOTH capture channels at the
    // projection (it drives `displayIndex`); prefer it, and only fall back to the
    // client-side per-segment derivation for legacy meetings that carry none.
    vm.firstSpokeSeconds = vm.firstSpokeSeconds ?? stat?.firstSpokeSeconds ?? null
    vm.longestSegment = stat?.longestSegment ?? null
    vm.segmentCount = stat?.segmentCount ?? 0
    vm.talkPercent = totalTalk > 0 ? Math.round((vm.talkSeconds / totalTalk) * 100) : null
  }
}

/**
 * Assign the 1-based unified display number to legacy (roster-derived) VMs, using
 * the SAME rank rule the backend applies to the unified `speakers[]` read model:
 * sort the real diarisation slots by `(firstSpokeSeconds ?? +Infinity, slot)` and
 * number them 1..N, so a meeting served before the read model existed still shows
 * unified "Speaker N" numbering. Synthetic analysis-only VMs (`slot === -1`) carry
 * no ordering signal and keep their `-1` sentinel. Runs AFTER
 * {@link attachSegmentStats} (it reads the attached first-spoke) and refreshes the
 * initials of any raw-label VM so its avatar can't disagree with the number.
 */
function assignLegacyDisplayIndexes(speakers: ISpeakerVM[]): void {
  const ranked = speakers
    .filter((vm) => vm.slot >= 0)
    .sort((a, b) => {
      const fa = a.firstSpokeSeconds ?? Number.POSITIVE_INFINITY
      const fb = b.firstSpokeSeconds ?? Number.POSITIVE_INFINITY
      if (fa !== fb) return fa - fb
      return a.slot - b.slot
    })
  ranked.forEach((vm, i) => {
    vm.displayIndex = i + 1
    vm.initials = speakerInitials(vm.displayName, vm.displayIndex)
  })
}

/**
 * One number per distinct person. The backend already numbers by entity, but a
 * raw placeholder ("Speaker 3", `Speaker_3`, `Remote::Speaker_3`) used to be
 * shown from the digits in the label, so two people both read "Speaker 3".
 *
 * Real slots (`slot >= 0`) are grouped by entity id, else label, else slot.
 * The same entity keeps one number. A positive `displayIndex` that nobody else
 * claimed is kept (no reshuffle). Collisions and missing numbers take the next
 * free number above the highest kept one. Analysis-only rows (`slot < 0`) stay
 * at the -1 sentinel.
 */
function disambiguateDisplayIndexes(speakers: ISpeakerVM[]): void {
  const real = speakers.filter((vm) => vm.slot >= 0)
  const groups = new Map<string, ISpeakerVM[]>()
  for (const vm of real) {
    const key =
      vm.id !== undefined && vm.id.length > 0
        ? `id:${vm.id}`
        : vm.label !== null && vm.label.length > 0
          ? `label:${vm.label}`
          : `slot:${vm.slot}`
    const list = groups.get(key)
    if (list) list.push(vm)
    else groups.set(key, [vm])
  }

  const ranked = [...groups.values()].sort((a, b) => {
    const first = (group: ISpeakerVM[]): number =>
      Math.min(...group.map((vm) => vm.firstSpokeSeconds ?? Number.POSITIVE_INFINITY))
    const fa = first(a)
    const fb = first(b)
    if (fa !== fb) return fa - fb
    const slotOf = (group: ISpeakerVM[]): number => Math.min(...group.map((vm) => vm.slot))
    return slotOf(a) - slotOf(b)
  })

  const used = new Set<number>()
  const assigned = new Map<ISpeakerVM, number>()
  for (const group of ranked) {
    const positives = group.map((vm) => vm.displayIndex).filter((n) => n > 0)
    const candidate = positives.length > 0 ? Math.min(...positives) : 0
    if (candidate > 0 && !used.has(candidate)) {
      used.add(candidate)
      for (const vm of group) assigned.set(vm, candidate)
    }
  }

  let next = used.size > 0 ? Math.max(...used) + 1 : 1
  for (const group of ranked) {
    const head = group[0]
    if (!head || assigned.has(head)) continue
    while (used.has(next)) next += 1
    used.add(next)
    for (const vm of group) assigned.set(vm, next)
    next += 1
  }

  for (const vm of real) {
    const n = assigned.get(vm)
    if (n === undefined || n === vm.displayIndex) continue
    vm.displayIndex = n
    vm.initials = speakerInitials(vm.displayName, n)
  }
}

/**
 * Cards look up colour by the translated display label ("Speaker 4" / "Spreker 4"),
 * while the map is otherwise keyed by the raw label. Register both UI locales
 * for the final display number. First speaker with that number wins.
 */
function registerDisplayIndexColors(colorMap: Map<string, string>, speakers: readonly ISpeakerVM[]): void {
  const seen = new Set<number>()
  for (const vm of speakers) {
    if (vm.displayIndex <= 0 || seen.has(vm.displayIndex)) continue
    seen.add(vm.displayIndex)
    colorMap.set(normalizeSpeakerKey(`Speaker ${vm.displayIndex}`), vm.resolvedColor)
    colorMap.set(normalizeSpeakerKey(`Spreker ${vm.displayIndex}`), vm.resolvedColor)
  }
}

/**
 * The user-facing label for a speaker: an unidentified diarisation slot that
 * carries a unified {@link ISpeakerEntry.displayIndex} renders as a localized
 * "Speaker {n}" (so two people never both show as "Speaker 3"); everything else
 * falls back to the cosmetic humanisation (raw labels lose their underscores,
 * named speakers keep their name). DISPLAY-ONLY — the raw `label` / `displayName`
 * stay the identity keys every mutation and the dialogue store key on.
 */
export function speakerDisplayLabel(vm: ISpeakerVM, i18n: I18n): string {
  // A stored raw placeholder ("Speaker 3", "Speaker_3") is still unnamed even
  // when status says named — unless the user typed that string themselves.
  const rawPlaceholder = isRawSpeakerLabel(vm.displayName) && vm.confirmedBy !== "user"
  const unnamed = vm.status === "unidentified" || vm.status === "suggested" || rawPlaceholder
  if (unnamed && vm.displayIndex > 0) {
    const displayIndex = vm.displayIndex
    return i18n._(msg`Speaker ${displayIndex}`)
  }
  return unnamed ? vm.displayName.replace(/_/g, " ") : vm.displayName
}

/**
 * One display label per speaker, in VM order. Not deduped: talk-share arrays
 * are zipped by index, so collapsing two "Speaker 4"s would attach the wrong
 * share. Headcount dedupes at the card.
 */
export function speakerDisplayNameList(speakers: readonly ISpeakerVM[], i18n: I18n): string[] {
  return speakers.map((vm) => speakerDisplayLabel(vm, i18n))
}

function tally(speakers: ISpeakerVM[]): { unidentifiedCount: number; suggestedCount: number } {
  let unidentifiedCount = 0
  let suggestedCount = 0
  for (const vm of speakers) {
    if (vm.status === "unidentified") unidentifiedCount++
    else if (vm.status === "suggested") suggestedCount++
  }
  return { unidentifiedCount, suggestedCount }
}

/**
 * The exact `speaker_names[slot]` key a rename / discard / merge mutation matches
 * on (the `from`). An UNRESOLVED slot still holds its raw diarisation label in the
 * roster — for a far-end voice that's the `Remote::`-prefixed label, which is
 * exactly `vm.label`. A RESOLVED (or discarded → "Unknown") slot holds its bare
 * display value. We never reattach `Remote::` to a NAME: that manufactured the
 * storage-form name the backend now rejects, and it wouldn't match a cleanly
 * renamed far-end roster entry (which stores the bare name) anyway.
 */
export function speakerRosterKey(vm: ISpeakerVM): string {
  return vm.label !== null && isRawSpeakerLabel(vm.displayName) ? vm.label : vm.displayName
}

/**
 * The owning entity id to key entity-route mutations on, or `undefined` when the
 * VM has none (a legacy meeting served without entity rows, or an entity
 * projection slot with no matching entity — both carry `id` absent or `""`).
 * Callers fall back to the name-keyed shims when this is `undefined`.
 */
export function speakerEntityId(vm: ISpeakerVM): string | undefined {
  return vm.id !== undefined && vm.id.length > 0 ? vm.id : undefined
}

/** One candidate the "Merge into…" affordance offers, resolved for the entity routes. */
export interface ISpeakerMergeOption {
  /**
   * Entity id of the target speaker to fold into, or `undefined` for a legacy
   * meeting without entity rows (the smart wrapper then merges by name-keyed shim).
   */
  entityId: string | undefined
  /** Bare display name shown on the merge button — never the `Remote::` storage form. */
  displayName: string
  /** True when every contributor to this name is a far-end voice (a display-only hint). */
  farEnd: boolean
}

/**
 * Build the "Merge into…" options for one speaker: every OTHER speaker, deduped by
 * display name. A room "Roderik" and a far-end "Roderik" collapse to ONE option
 * (merging into either folds to the same name) whose entity id prefers the
 * non-far-end contributor, and whose `farEnd` hint stays true only when EVERY
 * contributor is far-end. Purely VM-driven — it reads each VM's `displayName` /
 * `farEnd` / entity id, never a raw `Remote::` label string.
 */
export function buildMergeOptions(speakers: ISpeakerVM[], currentDisplayName: string): ISpeakerMergeOption[] {
  const byName = new Map<string, { entityId: string | undefined; farEnd: boolean }>()
  for (const vm of speakers) {
    if (vm.displayName === currentDisplayName) continue
    const entityId = speakerEntityId(vm)
    const prev = byName.get(vm.displayName)
    if (prev === undefined) {
      byName.set(vm.displayName, { entityId, farEnd: vm.farEnd })
    } else {
      // Prefer a resolved (non-far-end) entity id as the canonical merge target;
      // the name stays far-end only while no contributor is a room voice.
      byName.set(vm.displayName, {
        entityId: prev.farEnd && !vm.farEnd ? entityId : (prev.entityId ?? entityId),
        farEnd: prev.farEnd && vm.farEnd,
      })
    }
  }
  return [...byName.entries()].map(([displayName, v]) => ({ displayName, entityId: v.entityId, farEnd: v.farEnd }))
}

/**
 * Synthesise a VM for a name that has no diarisation slot — an analysis-only
 * owner (a summary card attributed to a name absent from the roster). `slot` is
 * -1 and `label` null; colour resolves through the standard chain.
 */
export function speakerVMFromName(name: string, colorMap?: Map<string, string>): ISpeakerVM {
  const farEnd = isFarEnd(name)
  const displayName = stripRemote(name)
  const status: TSpeakerEntryStatus = isUnidentifiedSpeaker(name) ? "unidentified" : "named"
  return {
    label: null,
    slot: -1,
    displayName,
    farEnd,
    status,
    recognitionSource: null,
    speakerId: null,
    color: null,
    title: null,
    talkSeconds: 0,
    // Wave-A placeholders — Wave B derives the client-side display number + presence.
    displayIndex: -1,
    presence: "unknown",
    resolvedColor: resolveSpeakerColor(name, colorMap),
    needsAttention: needsAttentionFor(status),
    initials: speakerInitials(displayName),
    // Synthetic (analysis-only) VMs have no diarisation slot → no segment stats.
    // `deriveMeetingSpeakers` fills `talkPercent` for VMs it derives; a standalone
    // caller gets the null defaults.
    firstSpokeSeconds: null,
    longestSegment: null,
    talkPercent: null,
    segmentCount: 0,
  }
}

/** PRIMARY path — 1:1 from the backend's unified `speakers[]`. */
function derivePrimary(entries: ISpeakerEntry[]): ISpeakerVMResult {
  const speakers: ISpeakerVM[] = []
  const byLabel = new Map<string, ISpeakerVM>()
  const byName = new Map<string, ISpeakerVM>()
  const colorMap = new Map<string, string>()

  // Reprocess used to keep unmatched named people as live label-less entities
  // (real slot, `label: null`) AND emit a fresh unidentified roster. Those
  // ghosts have no waveform band and double the speaker strip. Analysis-only
  // names stay (`slot === -1`). Teams/VTT meetings are all label-less, so the
  // filter only fires when at least one diarised label is present.
  const hasDiarized = entries.some((e) => e.label !== null)
  const visible = hasDiarized ? entries.filter((e) => e.label !== null || e.slot < 0) : entries

  for (const entry of visible) {
    // color override ?? slot palette (real diarisation slot) ?? name hash (label-null).
    const resolvedColor =
      entry.color ?? (entry.label !== null ? paletteColor(entry.slot) : speakerColor(entry.displayName))
    const vm: ISpeakerVM = {
      ...entry,
      resolvedColor,
      needsAttention: needsAttentionFor(entry.status),
      // Initials honour the backend's unified display number so the avatar and the
      // "Speaker N" name the chip renders can never diverge.
      initials: speakerInitials(entry.displayName, entry.displayIndex),
      // The backend computes first-spoke across both channels; keep it (it drives
      // `displayIndex`). `attachSegmentStats` fills the rest and only falls back to
      // segment-derived first-spoke when the backend carries none.
      firstSpokeSeconds: entry.firstSpokeSeconds,
      longestSegment: null,
      talkPercent: null,
      segmentCount: 0,
    }
    // Every slot survives in the array (merge duplicates stay distinct); the
    // lookup maps dedupe first-wins.
    speakers.push(vm)
    if (entry.label !== null && !byLabel.has(entry.label)) byLabel.set(entry.label, vm)
    if (!byName.has(vm.displayName)) byName.set(vm.displayName, vm)
    registerColor(colorMap, vm)
  }

  return { speakers, byLabel, byName, colorMap, ...tally(speakers) }
}

/** Best-effort string name from an untyped attendee-contribution entry. */
function contributionName(entry: unknown): string | null {
  if (typeof entry !== "object" || entry === null) return null
  if (!("name" in entry)) return null
  const name = (entry as { name: unknown }).name
  return typeof name === "string" ? name : null
}

/**
 * Reproduce the roster ∪ analysis name set that `$id.tsx` built inline: the
 * positional `speaker_names` roster, plus any names the analysis profiled
 * (attendee_contributions + speaker_quotes) that aren't already in the roster.
 * Exact-string membership, roster first — matching the original `Set` semantics.
 */
function collectLegacyNames(meeting: IDeriveMeetingInput): { rosterNames: string[]; allNames: string[] } {
  const rosterNames = meeting.speaker_names ?? []
  const names = new Set<string>(rosterNames)
  const analysis = meeting.analysis

  const contributions = analysis?.attendee_contributions
  if (contributions) {
    const normalized = normalizeAnalysis(
      { attendee_contributions: contributions },
      positionalSpeakerLabelMap(rosterNames, detectIndexBase(meeting.streaming_segments)),
    ).attendee_contributions
    if (Array.isArray(normalized)) {
      for (const entry of normalized) {
        const name = contributionName(entry)
        if (name) names.add(name)
      }
    }
  }

  for (const sq of coerceSpeakerQuotes(analysis?.speaker_quotes)) {
    if (sq.speaker) names.add(sq.speaker)
  }

  return { rosterNames, allNames: [...names] }
}

function legacyStatus(label: string, profile: ISpeakerProfile | undefined): TSpeakerEntryStatus {
  // Never "suggested" in the legacy world — the sidecar suggestion bucket is a
  // unified-model concept the pre-A5 payload doesn't carry.
  if (isUnidentifiedSpeaker(label)) return "unidentified"
  if (profile?.speaker_id) return "confirmed"
  return "named"
}

/** LEGACY path — mirrors the pre-unified derivation from `$id.tsx`. */
function deriveLegacy(meeting: IDeriveMeetingInput): ISpeakerVMResult {
  const { rosterNames, allNames } = collectLegacyNames(meeting)
  const rosterSet = new Set(rosterNames)
  const talkShare = meeting.talk_share ?? []
  const profiles = meeting.speaker_profiles ?? []
  const profileByName = new Map(profiles.map((p) => [p.name, p]))
  const profileColors = new Map(profiles.map((p) => [p.name, p.color] as [string, string | null]))
  const builtColorMap = buildSpeakerColorMap(allNames, profileColors)

  const speakers: ISpeakerVM[] = []
  const byLabel = new Map<string, ISpeakerVM>()
  const byName = new Map<string, ISpeakerVM>()
  const colorMap = new Map<string, string>()

  // Roster slots first — slot = positional index. Every slot survives in the
  // array (so a merge-by-rename that leaves a duplicate name keeps both slots
  // with their own positional talk_share); the lookup maps dedupe first-wins.
  rosterNames.forEach((raw, slot) => {
    const profile = profileByName.get(raw)
    const status = legacyStatus(raw, profile)
    const vm: ISpeakerVM = {
      label: raw,
      slot,
      displayName: stripRemote(raw),
      farEnd: isFarEnd(raw),
      status,
      recognitionSource: profile?.recognition_source ?? null,
      speakerId: profile?.speaker_id ?? null,
      color: profile?.color ?? null,
      title: profile?.title ?? null,
      talkSeconds: talkShare[slot] ?? 0,
      // Wave-A placeholders — Wave B derives the client-side display number + presence.
      displayIndex: -1,
      presence: "unknown",
      resolvedColor: resolveSpeakerColor(raw, builtColorMap),
      needsAttention: needsAttentionFor(status),
      initials: speakerInitials(stripRemote(raw)),
      // Filled by attachSegmentStats once every VM (and the meeting total) exists.
      firstSpokeSeconds: null,
      longestSegment: null,
      talkPercent: null,
      segmentCount: 0,
    }
    speakers.push(vm)
    if (!byLabel.has(raw)) byLabel.set(raw, vm)
    if (!byName.has(vm.displayName)) byName.set(vm.displayName, vm)
    registerColor(colorMap, vm)
  })

  // Analysis-only names (profiled by the LLM but absent from the roster) — no
  // slot, no profile, colour from the same built map.
  for (const name of allNames) {
    if (rosterSet.has(name)) continue
    const vm = speakerVMFromName(name, builtColorMap)
    speakers.push(vm)
    if (!byName.has(vm.displayName)) byName.set(vm.displayName, vm)
    registerColor(colorMap, vm)
  }

  return { speakers, byLabel, byName, colorMap, ...tally(speakers) }
}

/**
 * Derive the render-ready speaker view-model for a meeting. Uses the backend's
 * unified `speakers[]` when present, otherwise the legacy roster/analysis
 * derivation.
 */
export function deriveMeetingSpeakers(meeting: IDeriveMeetingInput): ISpeakerVMResult {
  const segStats = deriveSegmentStats(meeting.streaming_segments)
  const isPrimary = Boolean(meeting.speakers && meeting.speakers.length > 0)
  const result = isPrimary ? derivePrimary(meeting.speakers ?? []) : deriveLegacy(meeting)
  attachSegmentStats(result.speakers, segStats)
  // The primary path carries the backend's `displayIndex`; a legacy meeting has
  // none, so derive it client-side from the now-attached first-spoke stats using
  // the same rank rule, so old meetings also show unified "Speaker N" numbering.
  if (!isPrimary) assignLegacyDisplayIndexes(result.speakers)
  disambiguateDisplayIndexes(result.speakers)
  registerDisplayIndexColors(result.colorMap, result.speakers)
  return result
}
